/**
 * Reader archetypes: a deterministic identity computed from REAL local reading behaviour
 * (category mix, consistency, depth usage). The second shareable of the Signal recap:
 * evidence (Duolingo Year in Review) shows a personality-style share lifts sharing among
 * readers whose raw stats are modest. No invented numbers: every archetype line is backed
 * by the stat that earned it, and a reader with too little history gets none (fail closed).
 * House style: no em dashes or en dashes anywhere in copy.
 */

export interface ArchetypeInput {
  /** Distinct cards read per category (from locally seen cards). */
  categoryCounts: Record<string, number>;
  cardsRead: number;
  streak: number;
  savedCount: number;
  reactionsCount: number;
  /** Deep dives opened (locally counted). */
  divesOpened?: number;
  /** Good News cards read (locally counted). */
  goodNewsReads?: number;
}

export interface Archetype {
  id: string;
  name: string;
  /** One-line identity, written to be quotable on a share card. */
  line: string;
  /** The real stat that earned the archetype (renders under the name). */
  stat: string;
  /** A small emblem glyph for the share card. */
  emblem: string;
}

/** Minimum history before an identity is claimed. */
export const MIN_READS_FOR_ARCHETYPE = 8;

const CATEGORY_IDENTITIES: Record<string, Omit<Archetype, "stat">> = {
  "Science & Systems": {
    id: "systems-thinker",
    name: "The Systems Thinker",
    line: "You read for how things work, not just what happened.",
    emblem: "🔬",
  },
  "Technology & Tools": {
    id: "edge-scout",
    name: "The Edge Scout",
    line: "You spot the tools reshaping everything before most people hear of them.",
    emblem: "⚡",
  },
  "Power & Policy": {
    id: "power-watcher",
    name: "The Power Watcher",
    line: "You follow the decisions that move everything else.",
    emblem: "🏛️",
  },
  "Money & Work": {
    id: "market-mind",
    name: "The Market Mind",
    line: "You track where the money and the work are actually going.",
    emblem: "📈",
  },
  "Culture & Curiosity": {
    id: "culture-curator",
    name: "The Culture Curator",
    line: "You collect the stories that make people interesting at dinner.",
    emblem: "🎭",
  },
  "Sport & Play": {
    id: "clubhouse-brain",
    name: "The Clubhouse Brain",
    line: "You know the score and the story behind it.",
    emblem: "🏆",
  },
  "Planet & Environment": {
    id: "horizon-keeper",
    name: "The Horizon Keeper",
    line: "You watch the systems the rest of us stand on.",
    emblem: "🌏",
  },
};

function activeCategories(counts: Record<string, number>): [string, number][] {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
}

/** Compute the reader's archetype, or null when there is not enough history. */
export function computeArchetype(input: ArchetypeInput): Archetype | null {
  const reads = Math.max(0, input.cardsRead);
  if (reads < MIN_READS_FOR_ARCHETYPE) return null;
  const cats = activeCategories(input.categoryCounts);
  const totalCat = cats.reduce((s, [, n]) => s + n, 0);
  const [topCat, topN] = cats[0] ?? ["", 0];
  const topShare = totalCat > 0 ? topN / totalCat : 0;
  const dives = input.divesOpened ?? 0;
  const diveRate = reads > 0 ? dives / reads : 0;
  const goodNews = input.goodNewsReads ?? 0;

  // 1. Earned consistency beats everything: a double-digit streak is rare and real.
  if (input.streak >= 10) {
    return {
      id: "signal-hunter",
      name: "The Signal Hunter",
      line: "You show up for the signal every single day.",
      stat: `${input.streak} day streak · ${reads} stories`,
      emblem: "🎯",
    };
  }

  // 2. Depth: a reader who keeps opening the full story flow.
  if (dives >= 5 && diveRate >= 0.3) {
    return {
      id: "deep-diver",
      name: "The Deep Diver",
      line: "A headline is never enough for you. You want the whole flow.",
      stat: `${dives} deep dives opened`,
      emblem: "🐋",
    };
  }

  // 3. The bright-side reader.
  if (goodNews >= 5 && reads > 0 && goodNews / reads >= 0.25) {
    return {
      id: "grounded-optimist",
      name: "The Grounded Optimist",
      line: "You look for what is going right, and you find it.",
      stat: `${goodNews} good-news stories read`,
      emblem: "🌅",
    };
  }

  // 4. A dominant field claims its identity.
  if (topCat && topN >= 5 && topShare >= 0.35 && CATEGORY_IDENTITIES[topCat]) {
    const base = CATEGORY_IDENTITIES[topCat];
    return { ...base, stat: `${topN} ${topCat} stories · top field` };
  }

  // 5. Genuine breadth: reads across most fields with no single obsession.
  if (cats.length >= 5 && topShare <= 0.3) {
    return {
      id: "horizon-scanner",
      name: "The Horizon Scanner",
      line: "You read across every field, so nothing catches you off guard.",
      stat: `${cats.length} fields this month`,
      emblem: "🧭",
    };
  }

  // 6. Everyone with real history has at least this identity.
  return {
    id: "curious-reader",
    name: "The Curious Reader",
    line: "You follow the story wherever it leads.",
    stat: `${reads} stories read`,
    emblem: "✨",
  };
}
